/**
 * CopyButton — one-click "copy this value to clipboard" with check feedback.
 *
 * Sized to sit inline with mono-font IDs in a table cell, so the icon is
 * 12px and the button has zero outer padding. Briefly swaps to a checkmark
 * after a successful copy so the user knows it landed.
 */

import { useState } from 'react'
import { Check, Copy } from 'lucide-react'

import { cn } from '@/lib/utils'

interface CopyButtonProps {
  value: string
  /** Optional aria-label override; defaults to "Copy {label}" when provided. */
  label?: string
  className?: string
}

export function CopyButton({ value, label, className }: CopyButtonProps): React.JSX.Element {
  const [copied, setCopied] = useState(false)

  async function handleCopy(e: React.MouseEvent): Promise<void> {
    // Prevent the click from bubbling into a parent <Link>.
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => { setCopied(false) }, 1200)
    } catch (err) {
      // Clipboard write can fail (HTTP origin, denied permission). Don't
      // crash the page; the user can still select-and-copy the text.
      console.error('[CopyButton] clipboard write failed:', err)
    }
  }

  return (
    <button
      type="button"
      onClick={(e) => { void handleCopy(e) }}
      aria-label={label ?? `Copy ${value}`}
      className={cn(
        'inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
        className
      )}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </button>
  )
}
